import React from 'react';
import cx from 'classnames';
import Link from 'next/link';

import cypressIds from 'Components/cypress_ids.json';
import { applyCypressData } from 'Utilities/helpers';
import { useZendesk } from 'Utilities/hooks/useZendesk';

import styles from '../StaticContents.module.scss';

const Terms: React.FC = () => {
	const { adjustmentsAndCancellationsLink } = useZendesk();

	return (
		<div className={cx(styles.staticContents, styles.container)}>
			<h1>Motorway Pro - Terms and conditions</h1>
			<p>These Pro Terms were last updated on 1 May 2025</p>
			<ol className={styles.termsListRoot}>
				<li>
					<h3>Definitions and Interpretation</h3>
					<ol>
						<li>
							<div className={styles.termsKeys}>
								<div>In these Pro Terms, the following words have the following meanings:</div>
								<div>
									<strong>Applicable Laws: </strong>
									all laws, regulations, statutes, subordinate legislation, common law, judgments, orders, notices,
									instructions, decisions and awards of any court or competent authority or tribunal, all codes of
									practice having force of law, and any statutory guidance, regulatory policy or guidance and industry
									codes of practice. For the avoidance of doubt,
									<strong> Applicable Laws </strong>
									shall include but not be limited to consumer protection laws, sanctions, laws on anti-bribery and
									corruption, laws on anti-money laundering and the proceeds of crime, and laws on modern slavery;
								</div>
								<div>
									<strong>Bid: </strong>the highest sum offered by you on the Motorway Platform to purchase a Vehicle;
								</div>
								<div>
									<strong>Confidential Information: </strong>has the meaning given to it in Clause 7;
								</div>
								<div>
									<strong>Data Protection Laws: </strong>any applicable data protection, privacy, or similar laws that
									apply to data processed in connection with these Pro Terms, including the UK GDPR, the Data Protection
									Act 2018, the Privacy and Electronic Communications Directive 2002/58 and any amendments to these laws
									or replacements of these laws, including, without limitation, any variations, amendments or
									re-enactments;
								</div>
								<div>
									<strong>Dealer: </strong>you, as a dealer of used vehicles and an actual or prospective user of the
									Motorway Platform and purchaser or Seller of Vehicles through the same (also referred to in these Pro
									Terms as <strong>you, your, yourself</strong>);
								</div>
								<div>
									<strong>Dealer Code of Conduct: </strong>the code of conduct applicable to all Dealers available at{' '}
									<Link href="/code-of-conduct">https://pro.motorway.co.uk/code-of-conduct</Link>;
								</div>
								<div>
									<strong>Eligibility Criteria: </strong>the criteria, communicated to you by Motorway, which must be
									met in order for you to be allowed to access the Motorway Platform, and any services provided through
									the same, and bid on Vehicles (or sell Vehicles through Motorway Trade) which may include, but is not
									limited to, anti-money laundering checks, know-your-client checks, and other business-suitability
									checks conducted by Motorway and/or its third party representatives;
								</div>
								<div>
									<strong>Motorway: </strong>Motorway Online Ltd, a company registered in England and Wales with company
									number 10285711 whose registered office at 12-13 Wells Mews, London, W1T 3HE (also referred to in
									these Pro Terms as <strong>we, us, our</strong>);
								</div>
								<div>
									<strong>Motorway Move: </strong>means the Vehicle transport service provided by Motorway;
								</div>
								<div>
									<strong>Motorway Pay: </strong>the service made available by Motorway to enable the secure payment of
									the Purchase Price to a Seller;
								</div>
								<div>
									<strong>Motorway Platform: </strong>the online used car marketplace platform offered by Motorway via
									the Website;
								</div>
								<div>
									<strong>Motorway Products: </strong>any products and services offered by Motorway to Dealers, which
									shall include Motorway Move, Motorway Pay, Motorway Protect, and Motorway Trade, and any other
									products or services offered from time to time;
								</div>
								<div>
									<strong>Motorway Protect: </strong>the insurance product distributed by Motorway, available to Dealers
									on Motorway Pay who meet the relevant Eligibility Criteria;
								</div>
								<div>
									<strong>Motorway Trade: </strong>the service provided via the Motorway Platform allowing Dealers to
									sell Vehicles to other Dealers, subject to meeting specific Eligibility Criteria;
								</div>
								<div>
									<strong>Minimum Usage Requirements: </strong>means the minimum usage requirement for each of the
									Motorway Product(s), as communicated to you from time to time;
								</div>
								<div>
									<strong>Outstanding Finance: </strong>any finance due in respect of a Vehicle from a Seller or any
									previous or current owner of that Vehicle to a third party such as a car finance company, stocking
									finance company, or bank;
								</div>
								<div>
									<strong>Privacy Policy: </strong>our Dealer Privacy Policy which can be found at{' '}
									<Link href="/privacy">https://pro.motorway.co.uk/privacy</Link>;
								</div>
								<div>
									<strong>Pro Terms: </strong>these terms & conditions, together with our Privacy Policy, and any other
									policies referenced on this page and/or provided to you by Motorway from time to time;
								</div>
								<div>
									<strong>Purchase Fees: </strong>the sum(s) payable to Motorway by you in respect of each Vehicle
									purchased by you, as calculated in accordance with the Purchase Fee Schedule;
								</div>
								<div>
									<strong>Purchase Fee Schedule: </strong>the pricing table found at{' '}
									<Link href="/pricing">https://pro.motorway.co.uk/pricing</Link> or otherwise made available to you by
									Motorway, which sets out the specific Purchase Fees applicable, and as may be updated from time to
									time;
								</div>
								<div>
									<strong>Purchase Price: </strong>the consideration paid by you to a Seller for their Vehicle under a
									Vehicle Purchase Contract less applicable taxes;
								</div>
								<div>
									<strong>Seller: </strong>an individual or business who (directly or through another) sells their
									Vehicle through the Motorway Platform;
								</div>
								<div>
									<strong>Seller Vehicle Data: </strong>the information provided by the Seller in respect of their
									Vehicle;
								</div>
								<div>
									<strong>Transport Services: </strong>the collection of the Vehicle from a Seller by Motorway Move
									and/or its third-party transportation providers and the delivery of the same to you;
								</div>
								<div>
									<strong>Unacceptable Practices: </strong>any of the practices listed in Clause 13.4;
								</div>
								<div>
									<strong>VAT: </strong>means value added tax;
								</div>
								<div>
									<strong>Vehicle: </strong>any vehicle offered for sale by a Seller on the Motorway Platform;
								</div>
								<div>
									<strong>Vehicle Purchase Contract: </strong>an agreement or arrangement entered into between yourself
									and a Seller for the sale and purchase of a Vehicle;
								</div>
								<div>
									<strong>Website: </strong>the Motorway website available at{' '}
									<Link href="/">https://pro.motorway.co.uk/</Link> or such other URL as Motorway may determine; and
								</div>
								<div>
									<strong>Working Day: </strong>a day other than a Saturday, Sunday or public holiday in England.
								</div>
							</div>
						</li>
						<li>Words in the singular include the plural and in the plural include the singular.</li>
						<li>Headings shall not affect the interpretation of these Pro Terms.</li>
						<li>References to Clauses are, unless otherwise provided, references to the clauses of these Pro Terms.</li>
						<li>
							Unless a right or remedy of a party is expressed to be an exclusive right or remedy, the exercise of it by
							a party is without prejudice to that party's other rights and remedies.
						</li>
						<li>
							Any phrase introduced by the word 'including' shall be construed as illustrative and shall not limit the
							generality of the related general words.
						</li>
					</ol>
				</li>
				<li>
					<h3>Appointment and Use of the Motorway Platform</h3>
					<h4>
						<u>Motorway's Role</u>
					</h4>
					<ol>
						<li>
							You acknowledge that Motorway is the owner and operator of a used car marketplace, whereby you have the
							opportunity to purchase Vehicles offered for sale by Sellers. At no point does Motorway offer vehicles for
							sale itself, nor does it acquire any risk or title to any Vehicle made available for sale via the Motorway
							Platform.
						</li>
						<h4>
							<u>Dealer Eligibility and Conduct</u>
						</h4>
						<li>
							Subject to you meeting the Eligibility Criteria, Motorway shall permit you to access the Motorway Platform
							to enable you to place Bids and purchase Vehicles.
						</li>
						<li>
							You shall, at all times when using the Motorway Platform:
							<ol>
								<li>comply with these Pro Terms;</li>
								<li>comply with any policies provided to you from time to time by Motorway;</li>
								<li>continue to meet the Eligibility Criteria;</li>
								<li>not engage in any Unacceptable Practices; and</li>
								<li>
									act in good faith and treat our employees, transport partners, and Sellers with respect, act with
									honesty and integrity at all times, and comply with any conduct guidelines provided to you by Motorway
									from time to time (including the Dealer Code of Conduct).
								</li>
							</ol>
						</li>
						<li>
							Each party acknowledges and agrees that it has no authority, and shall not hold itself out, or permit any
							person to hold itself out, as being authorised to bind the other party in any way.
						</li>
						<h4>
							<u>Minimum Usage Requirements</u>
						</h4>
						<li>
							In respect of your Vehicle purchases, you shall comply with any Minimum Usage Requirement(s) that Motorway
							notifies to you from time to time.
						</li>
					</ol>
				</li>
				<li>
					<h3>Bid Process and Purchasing Vehicles</h3>
					<h4>
						<u>Vehicle Information</u>
					</h4>
					<ol>
						<li>
							On the Motorway Platform, you will be provided with the Seller Vehicle Data in respect of each Vehicle.
						</li>
						<li>
							You acknowledge and agree that each Vehicle is sold on an 'as is' basis. Motorway is a marketplace only
							and, as such, does not carry out any pre-sale inspection of Vehicles listed for sale on the Motorway
							Platform.
						</li>
						<li>
							If, in your reasonable opinion, further information is required about a particular Vehicle, you should
							request such information from us before you make a Bid, and we shall provide such further information as
							is made available to us from the Seller.
						</li>
						<li>
							You acknowledge and agree that you, alone, are responsible for carrying out your own due diligence on each
							Vehicle before you place a Bid.
						</li>
						<h4>
							<u>Placing a Bid</u>
						</h4>
						<li>
							If you wish to make a Bid for a Vehicle after you have carried out your due diligence, you shall do so via
							the Motorway Platform.
						</li>
						<h4>
							<u>Once a Bid Has Been Accepted</u>
						</h4>
						<li>
							Where a Seller accepts your Bid for their vehicle, we shall contact you and the Seller to inform each
							party of the sale. Within 1 (one) Working Day of receipt of that confirmation of sale, you must do the
							following:
							<ol>
								<li>
									contact the Seller to arrange for collection of the Vehicle (applicable if you choose to collect the
									Vehicle yourself); or
								</li>
								<li>
									contact us to arrange collection of the Vehicle (applicable if you would prefer Motorway Move to
									collect and drop the Vehicle off to you); and
								</li>
								<li>
									in either case, provide a copy of your terms and conditions of purchase in the form of a written
									Vehicle Purchase Contract to the Seller.
								</li>
							</ol>
						</li>
						<li>
							You warrant, represent, and undertake that your Vehicle Purchase Contract shall:
							<ol>
								<li>comply with all Applicable Laws, including Data Protection Laws;</li>
								<li>
									not permit you to cancel or terminate the Vehicle Purchase Contract without cause, except in
									accordance with Applicable Laws; and
								</li>
								<li>
									not include Motorway as a party and/or include any representations, warranties or conditions purported
									to be made by Motorway.
								</li>
							</ol>
						</li>
						<li>At our request, you shall provide us with a copy of your Vehicle Purchase Contract(s).</li>
						<h4>
							<u>Price Adjustments and Cancellation</u>
						</h4>
						<li>
							You acknowledge and accept that, save for where permitted under Clause 3.10, once a Bid has been accepted
							by a Seller, you shall purchase the Vehicle from the Seller and the Purchase Price for the Vehicle shall
							be the same as the winning Bid, accepted by the Seller.
						</li>
						<li>
							You shall only be entitled to cancel a Bid or seek an adjustment to the Purchase Price in the following
							circumstances:
							<ol>
								<li>the Seller refuses to sell the Vehicle;</li>
								<li>there is any undisclosed Outstanding Finance on the Vehicle;</li>
								<li>
									you can show that the Seller has engaged in fraudulent activity in selling the Vehicle (e.g., selling
									a clocked or stolen vehicle); and/or
								</li>
								<li>
									otherwise in accordance with our Motorway Cancellation and Price Adjustments Policy, a copy of which
									is available <Link href="/cancellation-and-price-adjustments">here</Link>.
								</li>
							</ol>
						</li>
						<li>
							Whenever you wish to make a cancellation or price adjustment under Clause 3.10:
							<ol>
								<li>
									you <u>must</u> complete the{' '}
									<Link
										passHref
										href={adjustmentsAndCancellationsLink}
										target="_blank"
										{...applyCypressData(cypressIds.links.adjustmentAndCancellations)}
									>
										Dealer Cancellation and Price Adjustment Form
									</Link>{' '}
									as soon as possible and, in any event, by no later than any time frame set out in the Motorway
									Cancellation and Price Adjustments Policy;
								</li>
								<li>
									you must always act in good faith and treat the Seller with fairness and respect at all times, in
									accordance with any guidelines and policies provided to you by Motorway from time to time;
								</li>
								<li>
									you must be able to demonstrate that the Seller Vehicle Data is incorrect or incomplete, including if
									there is undisclosed damage to, and/or faults with, the Vehicle.
								</li>
							</ol>
						</li>
						<li>
							All price adjustments based on Clause 3.10 must be fairly calculated to accurately account for the
							undisclosed damage and/or faults with the Vehicle.
						</li>
						<h4>
							<u>Discharging Outstanding Finance</u>
						</h4>
						<li>
							If the Seller Vehicle Data states that there is Outstanding Finance on a Vehicle, then you undertake to
							pay, in full, the sum due to the applicable finance company, and the Purchase Price paid to the Seller
							shall be reduced by the amount of the Outstanding Finance. You should obtain proof of payment of the
							Outstanding Finance from the applicable finance company which you shall make available to us and/or the
							Seller on request. You agree to indemnify us and hold us harmless against any losses, payments made by us
							where you have failed to pay them, penalties, fees, costs, interest, and damages arising out of or in
							connection with your breach of this Clause 3.13, and we may pursue any such amounts arising under this
							indemnity via a debt claim against you.
						</li>
						<h4>
							<u>Payment of the Purchase Price</u>
						</h4>
						<li>
							Notwithstanding any terms to the contrary in the Vehicle Sales Contract, you shall make payment of the
							Purchase Price during the collection of the relevant Vehicle from the Seller.
						</li>
						<li>
							Where a Vehicle is subject to VAT (e.g., a company Vehicle or a Vehicle used as part of a trade, or where
							the Seller is VAT registered), you agree that you shall pay VAT to the Seller in addition to the Purchase
							Price.
						</li>
						<li>
							The Purchase Price shall be paid by you to either:
							<ol>
								<li>the nominated bank account of the Seller, as notified to you by us; or</li>
								<li>
									if using Motorway Pay, your Motorway Pay account, as notified to you by us (see Clause 3.18 for more
									details).
								</li>
							</ol>
						</li>
						<li>At no point shall any of the Purchase Price for a Vehicle be made in cash.</li>
						<h4>
							<u>Motorway Pay</u>
						</h4>
						<li>
							If you are authorised by us to use Motorway Pay, then your payment of the Purchase Price will be handled
							by our third-party payments processor, Modulr. Please refer to the Motorway Pay Terms, available on our
							Website, for more detail.
						</li>
					</ol>
				</li>
				<li>
					<h3>Motorway Trade</h3>
					<div>
						If you wish to sell a Vehicle via Motorway Trade, you must first meet certain Eligibility Criteria (which
						are available on request from Motorway). The existing{' '}
						<Link href="https://motorway.co.uk/terms" target="_blank">
							Motorway Seller Terms
						</Link>{' '}
						will apply to any sales made through Motorway Trade.
					</div>
				</li>
				<li>
					<h3>Vehicle Collection and Transport</h3>
					<ol>
						<h4>
							<u>Collecting a Vehicle Yourself</u>
						</h4>
						<li>
							Once a Bid has been accepted by a Seller, if you decide to collect the Vehicle using your own staff or a
							third party appointed by yourself:
							<ol>
								<li>
									you shall collect the Vehicle as soon as possible after receipt of the confirmation of sale from us;
								</li>
								<li>
									you shall provide the Seller with regular collection updates and shall collect the Vehicle at a time
									suitable to the Seller; and
								</li>
								<li>
									you acknowledge and accept that you alone are responsible for carrying out an inspection of the
									Vehicle on collection. If you engage a third party agent to collect the Vehicle, it is your
									responsibility to ensure that a proper inspection of the Vehicle is undertaken by them. You shall
									notify us within 1 (one) Working Day of you having collected a Vehicle.
								</li>
							</ol>
						</li>
						<h4>
							<u>Motorway Move</u>
						</h4>
						<li>
							Motorway offers a fully managed Vehicle transportation and inspection service. You may, following a
							winning Bid having been accepted by a Seller, make a request to Motorway for Transport Services.
						</li>
						<li>
							When you make a request to Motorway for Transport Services, you acknowledge and accept that the{' '}
							<Link href="https://motorway.notion.site/Motorway-Transport-Information-c7c1c5dc3edb48a19af455774c96a594#7bbbe1ad499245e1909c1a7b891db9c1">
								Motorway Move Terms
							</Link>{' '}
							shall apply. You should review the Motorway Move Terms to make sure you understand the applicable
							conditions around, in particular, fees for Transport Services, service levels, and the damage claims
							process.
						</li>
						<h4>
							<u>Failure to Collect a Vehicle or Late Cancellation</u>
						</h4>
						<li>
							If, after 30 (thirty) days from the date on which a Bid has been accepted by a Seller, you either (i) fail
							to collect or arrange for collection using Motorway Move of a Vehicle, and/or (ii) cancel a Bid, Motorway
							shall be entitled to invoice you for the Purchase Fee for the relevant Vehicle.
						</li>
					</ol>
				</li>
				<li>
					<h3>Purchase Fees</h3>
					<ol>
						<h4>
							<u>Basis of Purchase Fees</u>
						</h4>
						<li>
							Unless you are enrolled for Motorway Pay the Purchase Fee payable by you to us for a Vehicle is based on
							the winning Bid price, and not the final Purchase Price (i.e., after any price negotiation).
						</li>
						<li>
							If you are enrolled for Motorway Pay, the Purchase Fee shall be based on the final Purchase Price and not
							the winning Bid price. This is because Motorway Pay gives us visibility on the Purchase Price.
						</li>
						<li>
							We may invoice you for the Purchase Fee from the earlier of:
							<ol>
								<li>
									notification from either yourself or the Seller that you have taken possession of the Vehicle from a
									Seller;
								</li>
								<li>
									payment of the Purchase Price, if you are on Motorway Pay (in which case, the Purchase Fee will be
									taken from your Motorway Pay wallet); or
								</li>
								<li>in accordance with Clause 5.4 above</li>
							</ol>
						</li>
						<h4>
							<u>Making Payment</u>
						</h4>
						<li>
							For the avoidance of doubt, the Purchase Fee shall not include other fees, including any fees relating to
							the provision of Transport Services, Motorway Protect, or fees relating to any other Motorway Services.
						</li>
						<li>
							All invoices submitted by Motorway shall be paid by you within 7 (seven) days of receipt, unless we agree
							to other payment terms.
						</li>
						<li>
							You agree that where you have either (i) cancelled your winning Bid , or (ii) offered a Purchase Price
							that is lower than the winning Bid, then the Purchase Fee for the Vehicle in question shall remain due and
							payable based on the winning Bid made by you in accordance with the Purchase Fee Schedule.
						</li>
						<li>
							If you fail at any time to pay any sum on the due date for payment, we shall be entitled to charge
							interest on any outstanding sum at the rate of 5% per annum above the prevailing base rate of Barclays
							Bank plc for the period from and including the due date to and including the date of receipt (whether
							before or after judgement).
						</li>
						<li>
							All sums payable are, unless specified otherwise on the Motorway Platform, exclusive of any value added
							tax or other applicable sales tax, which shall be added to the sum in question.
						</li>
						<h4>
							<u>Reporting and Audits</u>
						</h4>
						<li>
							You shall keep separate accounts and records in relation to your obligations under this Clause 6 for a
							rolling period of 6 (six) years. Motorway and/or its authorised nominee shall be entitled to inspect and
							audit such records on 5 (five) Working Days' notice during business hours for a period of 6 (six) years
							following the last Vehicle you have purchased from a Seller through the Motorway Platform. In particular,
							you shall allow Motorway to take copies of or extracts from such records. You shall cooperate fully with
							any such inspection or audit at all times.
						</li>
						<li>
							If any inspection carried out under Clause 6.9 demonstrates that any monies are owing to Motorway, you
							shall ensure that such monies are paid to us within 7 (seven) days of notification. Where any such
							inspection or audit is made necessary by your failure to supply a statement, or if the shortfall due to us
							is greater than 2% of the monies due in respect of the month in question, you shall reimburse us for the
							reasonable costs of any such inspection or audit.
						</li>
						<li>
							You acknowledge and agree that Motorway shall be entitled to amend the calculation of the Purchase Fees at
							any time on written notice to the Dealer but that any amendment shall not affect any Bid already submitted
							by you.
						</li>
					</ol>
				</li>
				<li>
					<h3>Confidential Information</h3>
					<ol>
						<li>
							Confidential Information shall mean all information, whether written or oral, and in whatever medium, and
							which relates to the business, Sellers, suppliers, products, financial and management affairs, dealers,
							employees or authorised agents, plans, proposals, strategies or trade secrets disclosed by one party (the{' '}
							<strong>Disclosing Party</strong>) to the other party (the <strong>Receiving Party</strong>).
						</li>
						<li>
							The Receiving Party shall not, and shall ensure that its Representatives (defined below) shall not, use,
							copy or disclose any of the Confidential Information of the Disclosing Party except to carry out its
							obligations and exercise its rights under the Pro Terms.
						</li>
						<li>
							The Receiving Party shall only disclose the Disclosing Party's Confidential Information to those of its
							employees, directors, external advisers, and subcontractors (<strong>Representatives</strong>), strictly
							to the extent that they need to know the same in order for the Receiving Party to carry out its
							obligations under the Pro Terms and where those Representatives are bound by written obligations of
							confidentiality and non-use and such obligations apply to the Confidential Information disclosed to them.
						</li>
						<li>
							The provisions of Clauses 7.1, 7.2 and 7.3 shall not apply to any Confidential Information which:
							<ol>
								<li>
									is or becomes generally available to the public other than as a result of any act or omission of the
									Receiving Party;
								</li>
								<li>
									is already in or comes into the possession of the Receiving Party from a person lawfully in possession
									of the information and owing no obligation of confidentiality to the Disclosing Party in respect of
									the information; or
								</li>
								<li>
									is required to be disclosed by any court, government or administrative authority competent to require
									disclosure.
								</li>
							</ol>
						</li>
					</ol>
				</li>
				<li>
					<h3>Data Protection</h3>
					<ol>
						<li>
							For the purposes of this Clause 8, “<strong>appropriate technical and organisational measures</strong>”, “
							<strong>controller</strong>”, “<strong>data subject</strong>”, “<strong>personal data</strong>”, “
							<strong>personal data breach</strong>”, “<strong>processing</strong>
							”, “<strong>process</strong>”, “<strong>processor</strong>” and “<strong>supervisory authority</strong>”
							shall have the meanings given to them in the Data Protection Laws.
						</li>
						<h4>
							<u>Mutual Obligations</u>
						</h4>
						<li>
							In order to facilitate your purchase of a Vehicle, you and we will be required to exchange personal data
							relating to the relevant Seller. With respect to such personal data, each of the parties agrees:
							<ol>
								<li>that the data specification at Clause 8.4 shall apply;</li>
								<li>
									that each of the parties shall be an independent controller of the personal data and shall comply
									with, and be separately responsible for complying with, their obligations under Data Protection Laws
									with respect to the personal data;
								</li>
								<li>that the personal data will be shared using secure methods as agreed in writing;</li>
								<li>
									in the event of a notice, request, dispute or claim brought by a data subject, supervisory authority,
									or other third party concerning the processing of the personal data against either or both parties,
									the parties will inform each other about such notice, dispute or claim, and will cooperate with each
									other to resolve the matter;
								</li>
								<li>
									to appoint a single point of contact for communications relating to the processing of personal data,
									and to inform the other party of such point of contact promptly upon entering into these Pro Terms.
									Our point of contact for communications relating to the processing of personal data is{' '}
									<Link href="mailto:legal@motorway.co.uk">legal@motorway.co.uk</Link>; and
								</li>
								<li>
									the personal data is provided by Motorway on an “as is” basis, to the fullest extent permissible
									pursuant to Applicable Law. Motorway disclaims all warranties and conditions express or implied,
									including, but not limited to, implied warranties of satisfactory quality and fitness for a particular
									purpose, in relation to the personal data, its use and the results of such use.
								</li>
							</ol>
						</li>
						<h4>
							<u>Your Obligations</u>
						</h4>
						<li>
							You agree:
							<ol>
								<li>
									that you shall only process the personal data in accordance with the principles of the UK GDPR, where
									you have a legal basis to do so under the UK GDPR, and exclusively for the purpose more particularly
									described in the data specification at Clause 8.4.3 (<strong>Agreed Purpose</strong>);
								</li>
								<li>
									to take reasonable steps to ensure the reliability of any employee or third party who may have access
									to the personal data, ensuring in each case that: (i) access is strictly limited to those individuals
									who need to know and/or access the relevant personal data, and (ii) all such individuals are subject
									to appropriate confidentiality undertakings, whether contractual or statutory;
								</li>
								<li>
									not to share the personal data with any third party other than with Motorway's express written
									consent;
								</li>
								<li>
									that you shall implement and maintain appropriate technical and organisational measures to protect the
									personal data against unauthorised or unlawful processing and against accidental loss or destruction,
									alteration or disclosure of, or damage;
								</li>
								<li>
									in relation to a personal data breach affecting the personal data, you shall: (i) notify Motorway
									without undue delay in the event of such personal data breach, setting out reasonable particulars, and
									to provide Motorway with any other information Motorway may reasonably request in relation to such
									personal data breach, and (ii) take immediate steps to mitigate and remediate such personal data
									breach, and to take such reasonable commercial steps as are directed by Motorway to assist in the
									mitigation and remediation of the personal data breach;
								</li>
								<li>
									not to transfer the personal data outside of the UK unless in compliance with Data Protection Laws;
									and
								</li>
								<li>
									to: (i) retain the personal data for no longer than is reasonably necessary for the Agreed Purpose,
									(ii) securely delete the personal data thereafter, and (iii) provide written confirmation of such
									secure deletion at Motorway's request.
								</li>
							</ol>
						</li>
						<h4>
							<u>Types of Personal Data we May Share</u>
						</h4>
						<li>
							Personal Data Specification:
							<ol>
								<li>
									<strong>Categories of data subject:</strong>
									<ol>
										<li>Sellers; and</li>
										<li>Drivers (if using Motorway Move).</li>
									</ol>
								</li>
								<li>
									<strong>Categories of Personal Data:</strong>
									<ol>
										<li>
											Seller: name, email address, address, phone number, date of birth, signature, photograph (on
											driving licence), bank account information, Seller Vehicle Data, and photographs or scans of
											documents containing the foregoing.{' '}
										</li>
										<li>Motorway Move Driver: real-time location, name, and phone number (if using Motorway Move).</li>
									</ol>
								</li>
								<li>
									<strong>Purpose of sharing Personal Data:</strong> in order to facilitate the purchase and transport
									of a Vehicle from a Seller.
								</li>
							</ol>
						</li>
					</ol>
				</li>
				<li>
					<h3>Warranties</h3>
					<ol>
						<li>
							Each of the parties represents, warrants and undertakes that:
							<ol>
								<li>
									it has the right, power and authority to enter into these Pro Terms and to perform fully all of its
									obligations under these Pro Terms; and
								</li>
								<li>the performance of these Pro Terms shall not breach any other agreement entered into by it.</li>
							</ol>
						</li>
						<li>
							You warrant, represent, and undertake that, as a business, you comply with Applicable Laws at all times.
						</li>
						<h4>
							<u>Disclaimer of Warranties</u>
						</h4>
						<li>
							We make no warranty or guarantee that a Seller will comply with the provisions of a Vehicle Purchase
							Contract. Accordingly, we shall not be liable for any costs, claims, losses, expenses or damages incurred
							by you as a result of any act or omission of a Seller.
						</li>
						<li>
							We make no warranty, representation, endorsement or recommendation in respect of any of the Seller Vehicle
							Data or any Vehicle. You agree that it is your responsibility to make appropriate enquiries to satisfy
							yourself that the Vehicle accords complies with the Seller Vehicle Data and otherwise meets your
							requirements:
							<ol>
								<li>prior to making a Bid; and</li>
								<li>
									again on collection of the Vehicle, unless you have elected to use Motorway Move, before paying the
									purchase price to the Seller.
								</li>
							</ol>
						</li>
						<li>
							Accordingly, you acknowledge and agree that Motorway shall not be liable for any costs, claims, losses,
							expenses, liabilities or damages incurred by you or any third party for any inaccuracies or discrepancies
							in the Seller Vehicle Data and/or the Vehicle itself.
						</li>
					</ol>
				</li>
				<li>
					<h3>Limit of Liability</h3>
					<h4>
						<u>Exclusions of Liability</u>
					</h4>
					<ol>
						<li>
							Neither party shall be liable to the other party under these Pro Terms, in contract, tort (including
							negligence), under statute or otherwise, in relation to its acts or omissions under these Pro Terms for
							any:
							<ol>
								<li>wasted expenditure;</li>
								<li>loss of opportunity;</li>
								<li>loss of data or content; and/or</li>
								<li>special, indirect or consequential loss.</li>
							</ol>
						</li>
						<h4>
							<u>Our Liability to You</u>
						</h4>
						<li>
							Our total liability arising out of or in connection with these Pro Terms or their subject matter, and to
							anything which we have done or not done in connection with the same (whether from breach of contract, tort
							(including negligence), breach of statutory duty or otherwise) shall be limited to:
							<ol>
								<li>
									the Purchase Fee paid by you to Motorway relating to the Vehicle that is the subject of the claim; or
								</li>
								<li>
									if the claim does not relate to a Vehicle, the total aggregate Purchase Fees paid by you to Motorway
									in the month preceding the date of the claim.
								</li>
							</ol>
						</li>
						<li>
							Nothing in these Pro Terms shall limit or exclude a party's liability for death or personal injury caused
							by negligence, fraud, fraudulent misrepresentation or any other loss that cannot lawfully be excluded or
							limited.
						</li>
					</ol>
				</li>
				<li>
					<h3>Termination and Suspension of Access</h3>
					<h4>
						<u>Rights of Termination</u>
					</h4>
					<ol>
						<li>
							Without prejudice to any other rights or remedies which may be available to it, either party shall be
							entitled to give notice in writing to the other party terminating the Pro Terms on giving the other 30
							(thirty) days' written notice.
						</li>
						<li>
							We may terminate these Pro Terms with you at any time with immediate effect where:
							<ol>
								<li>you no longer meet the Eligibility Criteria;</li>
								<li>
									any Purchase Fee remains outstanding for a period of 30 (thirty) days after the due date for payment;
								</li>
								<li>you fail to discharge any Outstanding Finance on a Vehicle within a reasonable timeframe;</li>
								<li>you breach any Applicable Laws; or</li>
								<li>
									we have a reasonable belief that your use of the Motorway Platform is, in any way, in material breach,
									or where there have been repeated breaches, of these Pro Terms or any of our guidelines or policies.
								</li>
							</ol>
						</li>
						<h4>
							<u>Consequences of Termination</u>
						</h4>
						<li>
							On termination of these Pro Terms:
							<ol>
								<li>
									the Purchase Fees shall remain due in respect of any winning Bid made by you that is accepted by a
									Seller prior to or on the termination date; and
								</li>
								<li>
									all outstanding sums due to Motorway shall immediately become payable, including any amounts owing
									under the indemnity at Clause 3.13.
								</li>
							</ol>
						</li>
						<li>
							Any provision of these Pro Terms that expressly or by implication is intended to come into or continue in
							force on or after termination of these Pro Terms and/or your revocation of access to the Motorway Platform
							shall remain in full force and effect.
						</li>
						<h4>
							<u>Suspension of Access to the Motorway Platform</u>
						</h4>
						<li>
							We may suspend your access without notice to the Motorway Platform and restrict your ability to place Bids
							on Vehicles, including cancelling any live Bids on Vehicles, in circumstances where we have a reasonable
							belief that your use of the Motorway Platform is, in any way, in contravention of these Pro Terms or any
							of our guidelines or policies, or whilst we investigate any potential breach of the same.
						</li>
					</ol>
				</li>
				<li>
					<h3>Force Majeure</h3>
					<ol>
						<li>
							For the purposes of this Clause 12 an event of “<strong>Force Majeure</strong>” means any event beyond the
							reasonable control of either party, including war, invasion, armed conflict, terrorism, strike, lock-out,
							labour dispute, (but excluding strikes, lockouts and labour disputes involving employees of the party
							affected) riot, civil commotion, accident, act of God, fire, flood, and storm.
						</li>
						<li>
							If a party is prevented, hindered or delayed from or in performing any of its obligations under these Pro
							Terms by an event of Force Majeure, the affected party's obligations under these Pro Terms are suspended
							while the event continues and to the extent that it is prevented, hindered or delayed and these Pro Terms
							may be terminated by the other party on written notice.
						</li>
						<li>A party may not rely on this Clause 12 for any failure to pay any sums due.</li>
					</ol>
				</li>
				<li>
					<h3>Use of the Motorway Platform</h3>
					<h4>
						<u>Our Website and its Content</u>
					</h4>
					<ol>
						<li>
							Motorway and/or its licensors own all intellectual property rights in the Website, Vehicle images,
							information and content available on the Website, any associated database(s), any proprietary software
							used within the Website and the underlying source code (the <strong>Motorway IP</strong>). All rights are
							reserved.
						</li>
						<li>
							Motorway grants you a revocable, non-exclusive, non-sub-licensable, non-transferable licence to use
							Motorway IP only to the extent necessary to retrieve, display and print content pages for your own
							personal, non-commercial use of the Website, subject always to compliance with these Pro Terms.
						</li>
						<li>
							For the avoidance of doubt, all Vehicle images belong to Motorway and cannot be used by you without our
							express written consent.
						</li>
						<h4>
							<u>Unacceptable Practices</u>
						</h4>
						<li>
							You shall (and shall procure that your employees or other persons engaged on your behalf shall), at all
							times:
							<ol>
								<li>not engage in any conduct that does not comply with the Dealer Code of Conduct;</li>
								<li>not engage in any deceptive practices or activities which breach Applicable Laws;</li>
								<li>
									not to try to gain or permit another to gain unauthorised access to the Website, the Motorway
									Platform, or any networks, servers or computer systems connected to the Website;
								</li>
								<li>
									not use or access the Website other than for the bona fide purpose of seeking to purchase Vehicles, in
									good faith, through the Motorway Platform;
								</li>
								<li>
									not to use the Website or Motorway Platform in any manner that could damage, disable, overburden or
									negatively impact the performance of our servers or networks or interfere with any other user's use
									and enjoyment of the Website;
								</li>
								<li>
									not, without our written permission, deploy or allow the deployment of any web scraping, screen
									crawling, or similar technology with the outcome or intention of using data from the the Website; and
								</li>
								<li>
									except to the extent expressly permitted by law and not capable of exclusion by law, copy, reproduce,
									redistribute, sell, create derivative works from, decompile, reverse engineer, or disassemble all or
									part of the Website.
								</li>
							</ol>
						</li>
						<li>
							For the avoidance of doubt, if you engage in any Unacceptable Practices, we will consider this a material
							breach of these Pro Terms and reserve our right to terminate or suspend your access to the Motorway
							Platform. We further reserve our right, in serious cases, to bring legal proceedings against you for
							breach of these Pro Terms and/or to refer you to law enforcement authorities where we reasonably believe
							that you have breached Applicable Laws.{' '}
						</li>
						<h4>
							<u>Website Availability</u>
						</h4>
						<li>
							The Website and Motorway Platform (including all content on it) is provided by us on an “as is” and “as
							available” basis. To the maximum extent permitted by Applicable Law, we disclaim all warranties,
							representations, and guarantees of any kind, either express or implied:
							<ol>
								<li>
									regarding the Website and Motorway Platform (including its content), including as to quality,
									suitability for any purpose (including your needs), compatibility, reliability, accuracy,
									completeness, timeliness, access or use of information provided by any Seller and/or is accessed or
									obtained by you via the Website, or otherwise through using the Motorway Services; or
								</li>
								<li>
									that the Website and Motorway Platform will operate continuously, without interruptions or be
									fault-free. From time to time, we may need to make the Website unavailable with or without notice to
									carry out maintenance or upgrade work. We accept no liability for any interruption or loss of service.
								</li>
							</ol>
						</li>
						<li>
							There may be occasions when access to the Website may be interrupted, including for scheduled maintenance
							or upgrades, for emergency repairs, or due to failure of telecommunications links and/or equipment. Where
							possible, we shall try to give you advance, reasonable notice of any such instances.
						</li>
						<li>
							We use reasonable endeavours to ensure that the Website does not contain or promulgate any viruses or
							other malicious code, but we cannot guarantee that the Website will be free from bugs, viruses or
							malicious code and we are not liable for any loss or damage caused by viruses or other technologically
							harmful material that may infect your computer equipment, programs or data due to your use of our Website.
						</li>
						<h4>
							<u>Tackling Fraud and Criminality</u>
						</h4>
						<li>
							We take instances of fraud and criminality on the Motorway Platform seriously. Your transactions may be
							frozen or cancelled should we reasonably suspect any instance of fraud, money laundering, proceeds of
							crime, breach of any company policies or the policies of our third party payments processing provider.
						</li>
					</ol>
				</li>
				<li>
					<h3>Complaints</h3>
					<div>
						If you are unhappy with your experience with Motorway, we encourage you to contact us directly by speaking
						to your account manager or by contacting our Dealer Support Team. Alternatively, you may email{' '}
						<Link href="mailto:dealercomplaints@motorway.co.uk">dealercomplaints@motorway.co.uk</Link>.
					</div>
				</li>
				<li>
					<h3>General</h3>
					<ol>
						<li>
							These Pro Terms contain the entire agreement of the parties with respect to their subject matter, apply to
							the exclusion of any terms provided by yourself, and supersede all prior agreements and representations,
							standard conditions or other implied conditions, whether written or oral, with respect to the subject
							matter of these Pro Terms. We may update these Pro Terms at any time, and shall provide you with written
							notice at the email address provided to us if the change to these Pro Terms is, in our reasonable view, a
							substantial change.
						</li>
						<li>
							You shall not assign or delegate your rights or obligations under these Pro Terms, in whole or in part, to
							any third party by operation of law or otherwise, without our prior written consent. Any attempted
							assignment or delegation that does not comply with this Clause 15.2 shall be of no effect.
						</li>
						<li>
							Any failure or delay to enforce any provision of these Pro Terms shall not constitute a waiver thereof or
							of any other provision.
						</li>
						<li>
							If any provision of these Pro Terms is found to be unenforceable, the remainder shall be enforced as fully
							as possible and the unenforceable provision shall be deemed modified to the limited extent required to
							permit its enforcement in a manner most closely approximating the intention of the parties.
						</li>
						<li>
							Nothing in these Pro Terms shall create or imply an agency, partnership or joint venture between the
							parties. Neither party shall act or describe itself as the agent of the other party nor shall either party
							have or represent that it has any authority to make commitments on behalf of the other. In providing
							payment processing services, we do not act as your agent.
						</li>
						<li>
							These Pro Terms are governed by and will be construed in accordance with the laws of England and Wales.
						</li>
						<li>
							The parties submit to the exclusive jurisdiction of the courts of England and Wales in relation to any
							legal actions or proceedings arising out of or in connection with these Pro Terms.
						</li>
					</ol>
				</li>
			</ol>
		</div>
	);
};

export default Terms;
